import { Injectable } from '@angular/core';
import { CombinedLookupDto } from '@core/services/api-clients';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from '@env';
import { PreFetchExecutionEvent } from '@core/execution-context';
import { IEngineODataState } from 'src/engine-sdk/contract/odata/iodata-state.model';
import { EngineODataStateParser } from '@core/engine-odata/services/odata-state-parser.service';
import { IGNORE_BUSY_INDICATOR } from '@core/layout/components/busy-indicator/busy-indicator.interceptor';
import { LodashService } from '@core/services/lodash.service';
import {
  IGroupedAutocompleteOption,
  IGroupedAutocompleteOptions,
} from '@shared/reactive-controls/components/grouped-autocomplete/grouped-autocomplete-control.model';

export interface ICombinedLookupsServiceState {
  value: any;
  searchText: string;
  odataQuery: IEngineODataState;
  lookups: CombinedLookupDto[];
}

@Injectable()
export class EngineCombinedLookupsService {
  private readonly BASE_URL = environment.urls.ODataUrl;
  private _stateBs: BehaviorSubject<ICombinedLookupsServiceState> = new BehaviorSubject({
    value: undefined,
    searchText: undefined,
    odataQuery: {
      take: 50,
      filter: <CompositeFilterDescriptor>{
        logic: 'and',
        filters: [],
      },
    },
    lookups: [],
  });
  private _groupedOptionsBs: BehaviorSubject<IGroupedAutocompleteOptions> = new BehaviorSubject({});
  private _preFetch: Subject<PreFetchExecutionEvent> = new Subject();

  state$ = this._stateBs.asObservable().pipe(distinctUntilChanged());
  groupedOptions$ = this._groupedOptionsBs.asObservable();
  preFetch$ = this._preFetch.asObservable();

  get currentState() {
    return this._stateBs.value;
  }
  get odataQuery() {
    return this.currentState.odataQuery;
  }

  constructor(private _http: HttpClient) {
    this.state$
      .pipe(
        filter((state) => !!state.lookups?.length),
        tap((state) => {
          if (Object.values(this._groupedOptionsBs.value).flatMap(x => x).find(x => x.value == state.value) == null) {
            this.fetchGroupedOptions(state.value, state.searchText)
          }
        })
      )
      .subscribe();
  }

  init(lookups: CombinedLookupDto[], value: any) {
    if (!lookups.length) return;

    this.currentState.lookups = lookups;
    this.currentState.value = value;

    this._stateBs.next(value ? { ...this.currentState, lookups, value } : this.currentState);
  }

  selectOption(value: any) {
    this._stateBs.next({ ...this.currentState, value, searchText: undefined });
  }

  clearOptionSelection() {
    this.currentState.value = null;
    this._stateBs.next(this.currentState);
  }

  search(text: string) {
    this.currentState.searchText = text;
    this._stateBs.next(this.currentState);

    this._preFetch.next(new PreFetchExecutionEvent(() => this.fetchGroupedOptions(this.currentState.value, text)));
  }

  addFilter(filter: CompositeFilterDescriptor) {
    this.currentState.odataQuery.filter.filters.push(filter);
    this._stateBs.next(this.currentState);
  }

  setFilter(filter: CompositeFilterDescriptor) {
    this.currentState.odataQuery.filter = filter;
    this._stateBs.next(this.currentState);
  }

  setCustomFilter(filter: string) {
    this.currentState.odataQuery.customFilter = filter;
    this._stateBs.next(this.currentState);
  }

  private fetchGroupedOptions(value: string, searchText: string): void {
    combineLatest(
      this.currentState.lookups.map((l) =>
        this.getOptionsAsync(l.attributeName, value ? value[l.attributeName] : undefined, searchText),
      ),
    )
      .pipe(
        tap((responses) => {
          const attributes = this.currentState.lookups.map((x) => x.attributeName);
          const newValue = {};
          responses.forEach((x, index) => (newValue[attributes[index]] = x));
          this._groupedOptionsBs.next(newValue);
        }),
      )
      .subscribe();
  }

  private getOptionsAsync(
    lookupId: string,
    selectedId: string,
    searchText: string,
  ): Observable<IGroupedAutocompleteOption[]> {
    if (!selectedId == undefined && searchText == undefined) return of([]);
    const lookup = this.currentState.lookups.find((l) => l.attributeName == lookupId);
    const searchState = selectedId
      ? this.getQueryByRecordId(selectedId)
      : this.getQueryBasedOnCurrentFiltersAndSearchTerm(searchText, lookup.showField);

    return this.query(lookup.targetEntity.name, lookup.showField, searchState).pipe(first());
  }

  private getQueryBasedOnCurrentFiltersAndSearchTerm(searchTerm: string, showfield: string): IEngineODataState {
    const currentState = LodashService.cloneDeep(this.currentState.odataQuery);
    if (searchTerm) {
      currentState.filter.filters.push({
        field: showfield,
        operator: 'contains',
        value: searchTerm,
      });
    }

    return currentState;
  }

  private getQueryByRecordId(recordId: string): IEngineODataState {
    if (!this.isGuid(recordId))
      throw {
        error: 'It is not possible to get the record into lookup because record id is invalid',
      };

    return {
      take: 1,
      skip: 0,
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'Id',
            operator: 'eq',
            value: recordId,
          },
        ],
      },
    };
  }

  private query(
    entityName: string,
    showField: string,
    state: IEngineODataState,
  ): Observable<IGroupedAutocompleteOption[]> {
    let url = `${this.BASE_URL}/${entityName}?${EngineODataStateParser.toODataString(
      state,
    )}& $select=Id, ${showField}& $orderby=${showField}`;

    url = url.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');

    const context = new HttpContext();
    context.set(IGNORE_BUSY_INDICATOR, true);
    return this._http.get(`${url}`, { context }).pipe(
      map((res) => res['value']),
      map((data) =>
        data.map((item) => {
          return { value: item.Id, label: item[showField] };
        }),
      ),
    );
  }

  private isGuid(string: string): boolean {
    if (string[0] === '{') string = string.substring(1, string.length - 1);

    const regexGuid =
      /^(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}$/gi;
    return regexGuid.test(string);
  }
}
